/**
 * Colores de las pantallas de acceso (doc 0390, ampliación).
 *
 * POR QUÉ EXISTE ESTE ARCHIVO
 *
 * Acceso, registro y recuperación de contraseña se escribieron con ~103 colores
 * fijos repartidos entre los tres, duplicando los mismos valores. El resultado:
 * quien tenía el panel en modo oscuro se topaba con tres pantallas en blanco
 * antes de entrar, y cualquier ajuste había que hacerlo tres veces.
 *
 * Aquí viven una sola vez, y responden al modo.
 *
 * CÓMO SE RESUELVEN
 *
 * Donde existe un token del sistema de temas se usa la variable CSS: cambia sola
 * con el modo y con el tema activo, sin que el componente tenga que saber en cuál
 * está. Cada `var()` lleva su valor de reserva por si el tema no cargó.
 *
 * Donde no hay token —los degradados decorativos del fondo, propios de estas
 * pantallas— se resuelve por modo. Inventar tokens para ellos sería ensuciar el
 * catálogo con algo que solo usan tres pantallas.
 *
 * NOTA: en el acceso todavía no hay sesión, así que el tema que se aplica es
 * siempre el de reserva (Clásico). El modo claro/oscuro sí es el que la persona
 * eligió, porque vive en el navegador.
 */
export function authPalette(mode) {
  const oscuro = mode === "dark";

  return {
    // Fondo de la pantalla. Los halos de color se conservan en ambos modos, pero
    // en oscuro pierden intensidad: sobre un fondo claro dan aire, sobre uno
    // oscuro llamarían demasiado la atención.
    pageBackground: oscuro
      ? "radial-gradient(1200px 500px at 20% 10%, rgba(59,130,246,0.10), transparent 60%)," +
        "radial-gradient(900px 400px at 80% 30%, rgba(16,185,129,0.07), transparent 55%)," +
        "linear-gradient(180deg, #0b1220 0%, #0f172a 100%)"
      : "radial-gradient(1200px 500px at 20% 10%, rgba(59,130,246,0.18), transparent 60%)," +
        "radial-gradient(900px 400px at 80% 30%, rgba(16,185,129,0.12), transparent 55%)," +
        "linear-gradient(180deg, #f7f9ff 0%, #edf2ff 100%)",

    // Tarjeta del formulario
    cardBackground: oscuro ? "rgba(30, 41, 59, 0.92)" : "rgba(255,255,255,0.92)",
    cardBackgroundSolid: oscuro ? "#111827" : "#ffffff",
    cardBorder: oscuro
      ? "1px solid rgba(148,163,184,0.16)"
      : "1px solid rgba(15,23,42,0.06)",
    cardShadow: oscuro
      ? "0 18px 60px rgba(0, 0, 0, 0.45)"
      : "0 18px 60px rgba(15, 23, 42, 0.10)",

    // Texto
    textStrong: "var(--wtx-text-primary, #0f172a)",
    textMedium: oscuro ? "rgba(226,232,240,0.78)" : "rgba(15,23,42,0.75)",
    textMuted: "var(--wtx-text-muted, #64748b)",

    // Campos
    inputBackground: oscuro ? "rgba(15,23,42,0.65)" : "#fff",
    inputBorder: oscuro ? "rgba(148,163,184,0.25)" : "rgba(15,23,42,0.15)",

    // Acento: sale del tema, así que una empresa con color propio también lo ve
    // en su pantalla de acceso.
    accent: "var(--wtx-accent, #2563eb)",
    accentContrast: "var(--wtx-accent-contrast, #ffffff)",
    accentGradient: oscuro
      ? "linear-gradient(45deg, #1d4ed8 30%, #1e40af 90%)"
      : "linear-gradient(45deg, #2563eb 30%, #1d4ed8 90%)",

    // Panel decorativo de la derecha
    panelGlass: oscuro ? "rgba(30,41,59,0.55)" : "rgba(255,255,255,0.55)",
    panelCard: oscuro ? "rgba(15,23,42,0.85)" : "#ffffff",
    panelCardBorder: oscuro
      ? "1px solid rgba(148,163,184,0.14)"
      : "1px solid rgba(15,23,42,0.05)",
    panelSkeleton: oscuro ? "rgba(148,163,184,0.20)" : "#e5e7eb",

    // Pie de página
    chipBackground: oscuro ? "#1e293b" : "#0f172a",
    chipText: "#ffffff"
  };
}

export default authPalette;
